import { zodResolver } from '@hookform/resolvers/zod';
import { CreateVehicleSchema, FuelType, TrackingSource, VehicleType, type CreateVehicle } from '@rfleet/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { Camera, Pencil, Plus, Radio, Smartphone, Trash2, Truck } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Can } from '../../app/guards';
import { Badge, Dialog, EmptyState, Field, PageHeader, Pagination, Skeleton, Spinner, Table, Td, Th, useToast } from '../../components/ui';
import { api, qs, type Paginated } from '../../lib/api';
import { fmtNumber } from '../../lib/format';
import { uploadFile } from '../../lib/upload';

export interface VehicleRow {
  id: string;
  plateAr: string;
  plateLatin: string | null;
  name: string | null;
  type: string;
  make: string | null;
  model: string | null;
  year: number | null;
  status: 'ACTIVE' | 'MAINTENANCE' | 'INACTIVE';
  trackingSource: 'PHONE' | 'OBD' | 'BOTH';
  odometerKm: string;
  fuelType: string | null;
  tankCapacityL: string | null;
  speedLimitKph: number | null;
  vin: string | null;
  notes: string | null;
  photoFileId: string | null;
  photoUrl: string | null;
  groupId: string | null;
  group: { id: string; name: string; color: string | null } | null;
  currentDriver: { id: string; fullName: string; phone: string } | null;
  device: { id: string; imei: string; status: string; lastSeenAt: string | null } | null;
  liveState: { status: string; statusSince: string | null; lastAt: string | null; speedKph: number | null; activeSource: string | null } | null;
}
export interface Group { id: string; name: string; color: string | null; _count?: { vehicles: number } }

export const vehicleStatusTone = (s: VehicleRow['status']) => (s === 'ACTIVE' ? 'success' : s === 'MAINTENANCE' ? 'warning' : 'neutral') as 'success' | 'warning' | 'neutral';

export function VehicleThumb({ plate, photoUrl, size = 'md' }: { plate: string; photoUrl: string | null; size?: 'md' | 'lg' }) {
  const cls = clsx('shrink-0 overflow-hidden rounded-lg bg-accent text-accent-ink flex items-center justify-center', size === 'lg' ? 'size-16' : 'size-10');
  return photoUrl ? <img src={photoUrl} alt={plate} className={clsx(cls, 'object-cover')} /> : <span className={cls} aria-hidden><Truck size={size === 'lg' ? 28 : 18} /></span>;
}

export function SourceBadge({ source }: { source: VehicleRow['trackingSource'] }) {
  const { t } = useTranslation();
  return (
    <span className="inline-flex items-center gap-1 text-xs text-text-muted" title={t(`vehicles.sources.${source}`)}>
      {source !== 'OBD' ? <Smartphone size={14} aria-hidden /> : null}
      {source !== 'PHONE' ? <Radio size={14} aria-hidden /> : null}
      <span className="sr-only">{t(`vehicles.sources.${source}`)}</span>
    </span>
  );
}

const ARABIC_LETTER_RE = /[a-zA-Zء-ي]/;
const DIGIT_RE = /[0-9٠-٩۰-۹]/;

function toWesternDigit(ch: string): string {
  const code = ch.codePointAt(0)!;
  if (code >= 0x0660 && code <= 0x0669) return String(code - 0x0660);
  if (code >= 0x06f0 && code <= 0x06f9) return String(code - 0x06f0);
  return ch;
}

function parsePlateAr(value: string): string[] {
  const letters: string[] = [];
  const digits: string[] = [];
  for (const ch of value) {
    if (DIGIT_RE.test(ch)) {
      if (digits.length < 3) digits.push(toWesternDigit(ch));
    } else if (ARABIC_LETTER_RE.test(ch)) {
      if (letters.length < 3) letters.push(ch);
    }
  }
  while (letters.length < 3) letters.push('');
  while (digits.length < 3) digits.push('');
  return [...letters, ...digits];
}

function cellsToPlateAr(cells: string[]): string {
  const letters = cells.slice(0, 3).filter(Boolean).join(' ');
  const digits = cells.slice(3).filter(Boolean).join('');
  return [letters, digits].filter(Boolean).join(' ');
}

/** OTP-style split entry for the Arabic plate: one box per letter/digit. */
function PlateArInput({ id, value, onChange }: { id: string; value: string; onChange: (v: string) => void }) {
  const [cells, setCells] = useState<string[]>(() => parsePlateAr(value));
  const refs = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    const next = parsePlateAr(value);
    setCells((prev) => (prev.join('|') === next.join('|') ? prev : next));
  }, [value]);

  const emit = (next: string[]) => {
    setCells(next);
    onChange(cellsToPlateAr(next));
  };

  const handleInput = (i: number, raw: string) => {
    const isDigitCell = i >= 3;
    const matcher = isDigitCell ? DIGIT_RE : ARABIC_LETTER_RE;
    const chars = raw
      .split('')
      .filter((c) => matcher.test(c))
      .map((c) => (isDigitCell ? toWesternDigit(c) : c));
    const next = [...cells];
    if (!chars.length) {
      next[i] = '';
      emit(next);
      return;
    }
    let idx = i;
    for (const c of chars) {
      if (idx > 5) break;
      next[idx] = c;
      idx++;
    }
    emit(next);
    refs.current[Math.min(idx, 5)]?.focus();
  };

  const handleKeyDown = (i: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !cells[i] && i > 0) {
      const next = [...cells];
      next[i - 1] = '';
      emit(next);
      refs.current[i - 1]?.focus();
    }
  };

  const { t } = useTranslation();
  const SAMPLE_LETTERS = ['أ', 'ب', 'ح'];

  const box = (i: number, groupId?: string) => (
    <input
      key={i}
      id={groupId}
      ref={(el) => {
        refs.current[i] = el;
      }}
      value={cells[i]}
      onChange={(e) => handleInput(i, e.target.value)}
      onKeyDown={(e) => handleKeyDown(i, e)}
      onFocus={(e) => e.currentTarget.select()}
      placeholder={i >= 3 ? `${i - 2}` : SAMPLE_LETTERS[i]}
      className="input w-10 !px-0 text-center text-lg placeholder:text-text-faint/50"
      dir={i >= 3 ? 'ltr' : undefined}
      inputMode={i >= 3 ? 'numeric' : 'text'}
      aria-label={i >= 3 ? t('vehicles.plateDigit', { n: i - 2 }) : t('vehicles.plateLetter', { n: i + 1 })}
    />
  );

  return (
    <div className="flex items-center gap-2">
      <div className="flex gap-1.5">{[0, 1, 2].map((i) => box(i, i === 0 ? id : undefined))}</div>
      <span className="h-5 w-px shrink-0 bg-border" aria-hidden />
      <div className="flex gap-1.5">{[3, 4, 5].map((i) => box(i))}</div>
    </div>
  );
}

export function VehiclesPage() {
  const { t, i18n } = useTranslation();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [groupId, setGroupId] = useState('');
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<VehicleRow | null>(null);
  const q = useQuery({ queryKey: ['vehicles', page, search, status, groupId], queryFn: () => api<Paginated<VehicleRow>>(`/vehicles${qs({ page, q: search, status, groupId })}`) });
  const groups = useQuery({ queryKey: ['vehicle-groups'], queryFn: () => api<Group[]>('/vehicle-groups') });

  return (
    <div className="flex flex-col gap-4">
      <PageHeader
        title={t('nav.vehicles')}
        subtitle={q.data ? t('common.totalCount', { count: q.data.meta.total }) : undefined}
        actions={
          <div className="flex gap-2">
            <Link to="/vehicles/groups" className="btn-secondary">{t('vehicles.groups')}</Link>
            <Can perm="vehicles:create"><button className="btn-primary" onClick={() => setCreating(true)}><Plus size={16} aria-hidden /> {t('vehicles.add')}</button></Can>
          </div>
        }
      />
      <div className="flex flex-wrap gap-2">
        <input className="input max-w-xs" placeholder={t('vehicles.searchPlaceholder')} value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} aria-label={t('common.search')} />
        <select className="input max-w-44" value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }} aria-label={t('settings.users.status')}>
          <option value="">{t('common.all')} — {t('settings.users.status')}</option>
          {(['ACTIVE', 'MAINTENANCE', 'INACTIVE'] as const).map((s) => <option key={s} value={s}>{t(`vehicles.statuses.${s}`)}</option>)}
        </select>
        <select className="input max-w-48" value={groupId} onChange={(e) => { setGroupId(e.target.value); setPage(1); }} aria-label={t('vehicles.group')}>
          <option value="">{t('common.all')} — {t('vehicles.group')}</option>
          {groups.data?.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
        </select>
      </div>

      {q.isLoading ? <Skeleton className="h-72" /> : !q.data?.data.length ? (
        <div className="card"><EmptyState title={t('vehicles.empty')} hint={t('vehicles.emptyHint')} /></div>
      ) : (
        <Table>
          <thead><tr><Th>{t('vehicles.plate')}</Th><Th>{t('vehicles.vehicle')}</Th><Th>{t('vehicles.group')}</Th><Th>{t('vehicles.driver')}</Th><Th>{t('vehicles.source')}</Th><Th>{t('vehicles.odometer')}</Th><Th>{t('settings.users.status')}</Th><Th className="w-1" sticky="end" /></tr></thead>
          <tbody>
            {q.data.data.map((v) => (
              <tr key={v.id} className="group hover:bg-surface-hover">
                <Td>
                  <div className="flex items-center gap-3">
                    <VehicleThumb plate={v.plateAr} photoUrl={v.photoUrl} />
                    <div><Link to={`/vehicles/${v.id}`} className="font-bold text-brand-ink hover:underline">{v.plateAr}</Link>{v.plateLatin ? <div className="text-xs text-text-faint" dir="ltr">{v.plateLatin}</div> : null}</div>
                  </div>
                </Td>
                <Td>{[v.make, v.model, v.year].filter(Boolean).join(' ') || t(`vehicles.types.${v.type}`)}{v.name ? <div className="text-xs text-text-muted">{v.name}</div> : null}</Td>
                <Td>{v.group ? <span className="inline-flex items-center gap-1.5"><span className="size-2 rounded-full" style={{ background: v.group.color ?? '#94A3B8' }} />{v.group.name}</span> : '—'}</Td>
                <Td>{v.currentDriver ? <Link to={`/drivers/${v.currentDriver.id}`} className="hover:underline">{v.currentDriver.fullName}</Link> : <span className="text-text-faint">—</span>}</Td>
                <Td><SourceBadge source={v.trackingSource} />{v.device ? <div className="text-[11px] text-text-faint" dir="ltr">{v.device.imei}</div> : null}</Td>
                <Td>{fmtNumber(v.odometerKm, i18n.language)} <span className="text-xs text-text-faint">{t('units.km')}</span></Td>
                <Td><Badge tone={vehicleStatusTone(v.status)} dot>{t(`vehicles.statuses.${v.status}`)}</Badge></Td>
                <Td className="text-end" sticky="end"><Can perm="vehicles:update"><button className="btn-ghost size-8 !p-0" onClick={() => setEditing(v)} aria-label={t('common.edit')} title={t('common.edit')}><Pencil size={14} /></button></Can></Td>
              </tr>
            ))}
          </tbody>
          <tfoot><tr><td colSpan={8}><Pagination page={q.data.meta.page} pageSize={q.data.meta.pageSize} total={q.data.meta.total} onPage={setPage} /></td></tr></tfoot>
        </Table>
      )}
      {creating ? <VehicleDialog groups={groups.data ?? []} onClose={() => setCreating(false)} /> : null}
      {editing ? <VehicleDialog vehicle={editing} groups={groups.data ?? []} onClose={() => setEditing(null)} /> : null}
    </div>
  );
}

export function VehicleDialog({ vehicle, groups, onClose }: { vehicle?: VehicleRow; groups: Group[]; onClose: () => void }) {
  const { t } = useTranslation();
  const toast = useToast();
  const qc = useQueryClient();
  const isNew = !vehicle;
  const [photo, setPhoto] = useState<{ fileId: string | null; url: string | null }>({ fileId: vehicle?.photoFileId ?? null, url: vehicle?.photoUrl ?? null });
  const [uploading, setUploading] = useState(false);
  const form = useForm<CreateVehicle>({
    resolver: zodResolver(isNew ? CreateVehicleSchema : CreateVehicleSchema.omit({ odometerKm: true }).partial()),
    defaultValues: vehicle
      ? { plateAr: vehicle.plateAr, plateLatin: vehicle.plateLatin ?? '', name: vehicle.name ?? '', type: vehicle.type as CreateVehicle['type'], make: vehicle.make ?? '', model: vehicle.model ?? '', year: vehicle.year ?? undefined, fuelType: (vehicle.fuelType as CreateVehicle['fuelType']) ?? undefined, tankCapacityL: vehicle.tankCapacityL != null ? Number(vehicle.tankCapacityL) : undefined, trackingSource: vehicle.trackingSource, groupId: vehicle.groupId, speedLimitKph: vehicle.speedLimitKph ?? null, vin: vehicle.vin ?? '', notes: vehicle.notes ?? '' }
      : { type: 'TRUCK', trackingSource: 'PHONE', odometerKm: 0, fuelType: 'DIESEL' },
  });
  const save = useMutation({
    mutationFn: (v: CreateVehicle) => {
      const body = { ...v, plateLatin: v.plateLatin || undefined, name: v.name || undefined, make: v.make || undefined, model: v.model || undefined, vin: v.vin || undefined, year: v.year || undefined, groupId: v.groupId || null, photoFileId: photo.fileId };
      return isNew ? api('/vehicles', { method: 'POST', json: body }) : api(`/vehicles/${vehicle.id}`, { method: 'PATCH', json: body });
    },
    onSuccess: () => { toast.success(t('common.saved')); void qc.invalidateQueries({ queryKey: ['vehicles'] }); void qc.invalidateQueries({ queryKey: ['vehicle', vehicle?.id] }); onClose(); },
    onError: toast.error,
  });
  const onPhoto = async (file: File | undefined) => {
    if (!file) return;
    setUploading(true);
    try {
      const fileId = await uploadFile(file, 'vehicle_photo');
      setPhoto({ fileId, url: URL.createObjectURL(file) });
    } catch (err) {
      toast.error(err);
    } finally {
      setUploading(false);
    }
  };
  const e = form.formState.errors;
  const num = { setValueAs: (v: unknown) => (v === '' || v == null ? undefined : Number(v)) };
  const plate = form.watch('plateAr') || vehicle?.plateAr || '';
  return (
    <Dialog open onClose={onClose} title={isNew ? t('vehicles.add') : t('vehicles.edit')} size="lg" footer={<><button className="btn-secondary" onClick={onClose}>{t('common.cancel')}</button><button className="btn-primary" form="vehicle-form" type="submit" disabled={save.isPending || uploading}>{save.isPending ? <Spinner /> : null}{t('common.save')}</button></>}>
      <form id="vehicle-form" onSubmit={form.handleSubmit((v) => save.mutate(v))} className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" noValidate>
        <div className="flex items-center gap-4 sm:col-span-2 lg:col-span-3">
          <VehicleThumb plate={plate} photoUrl={photo.url} size="lg" />
          <div className="flex flex-wrap gap-2">
            <label className={clsx('btn-secondary cursor-pointer', uploading && 'pointer-events-none opacity-60')}>
              {uploading ? <Spinner /> : <Camera size={16} />} {photo.url ? t('vehicles.changePhoto') : t('vehicles.addPhoto')}
              <input type="file" accept="image/*" className="sr-only" onChange={(ev) => { void onPhoto(ev.target.files?.[0]); ev.target.value = ''; }} />
            </label>
            {photo.url ? <button type="button" className="btn-ghost text-danger-fg" onClick={() => setPhoto({ fileId: null, url: null })}><Trash2 size={16} /> {t('vehicles.removePhoto')}</button> : null}
          </div>
        </div>
        <Field label={t('vehicles.plateAr')} required error={e.plateAr?.message} hint={t('vehicles.plateHint')}>
          {(id) => <Controller control={form.control} name="plateAr" render={({ field }) => <PlateArInput id={id} value={field.value ?? ''} onChange={field.onChange} />} />}
        </Field>
        <Field label={t('vehicles.plateLatin')} error={e.plateLatin?.message}>{(id) => <input id={id} dir="ltr" className="input" placeholder="ABH 123" {...form.register('plateLatin')} />}</Field>
        <Field label={t('vehicles.name')} error={e.name?.message}>{(id) => <input id={id} className="input" {...form.register('name')} />}</Field>
        <Field label={t('vehicles.type')} required>{(id) => <select id={id} className="input" {...form.register('type')}>{VehicleType.map((x) => <option key={x} value={x}>{t(`vehicles.types.${x}`)}</option>)}</select>}</Field>
        <Field label={t('vehicles.make')}>{(id) => <input id={id} className="input" dir="auto" {...form.register('make')} />}</Field>
        <Field label={t('vehicles.model')}>{(id) => <input id={id} className="input" dir="auto" {...form.register('model')} />}</Field>
        <Field label={t('vehicles.year')} error={e.year?.message}>{(id) => <input id={id} type="number" dir="ltr" className="input" {...form.register('year', num)} />}</Field>
        <Field label={t('vehicles.fuelType')} error={e.fuelType?.message}>{(id) => <select id={id} className="input" {...form.register('fuelType', { setValueAs: (v: string) => (v === '' ? undefined : v) })}><option value="">—</option>{FuelType.map((x) => <option key={x} value={x}>{t(`vehicles.fuelTypes.${x}`)}</option>)}</select>}</Field>
        <Field label={t('vehicles.tankCapacity')} error={e.tankCapacityL?.message}>{(id) => <input id={id} type="number" dir="ltr" className="input" {...form.register('tankCapacityL', num)} />}</Field>
        <Field label={t('vehicles.source')} required hint={t('vehicles.sourceHint')}>{(id) => <select id={id} className="input" {...form.register('trackingSource')}>{TrackingSource.map((x) => <option key={x} value={x}>{t(`vehicles.sources.${x}`)}</option>)}</select>}</Field>
        <Field label={t('vehicles.group')}>{(id) => <select id={id} className="input" {...form.register('groupId')}><option value="">—</option>{groups.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}</select>}</Field>
        {isNew ? <Field label={t('vehicles.odometer')} error={e.odometerKm?.message}>{(id) => <input id={id} type="number" dir="ltr" className="input" {...form.register('odometerKm', { valueAsNumber: true })} />}</Field> : null}
        <Field label={t('vehicles.speedLimit')} hint={t('vehicles.speedLimitHint')} error={e.speedLimitKph?.message}>{(id) => <input id={id} type="number" dir="ltr" className="input" {...form.register('speedLimitKph', { setValueAs: (v: unknown) => (v === '' || v == null ? null : Number(v)) })} />}</Field>
        <Field label={t('vehicles.vin')}>{(id) => <input id={id} dir="ltr" className="input" {...form.register('vin')} />}</Field>
        <div className="sm:col-span-2 lg:col-span-3"><Field label={t('vehicles.notes')}>{(id) => <textarea id={id} className="input min-h-20" {...form.register('notes')} />}</Field></div>
      </form>
    </Dialog>
  );
}
